import { useEffect, useState } from "react";
import Form from "../../../Components/Form";
import StaticDropdown from "../../../Components/Dropdown/StaticDropdown";
import TextInput from "../../../Components/TextInput";
import Button from "../../../Components/Button";
import { useAppDispatch, useAppSelector } from "../../../App/hooks";
import {
  StepOrderSide,
  VolumeCalculationType,
  IStepOptionDropdownItem,
} from "../../../Interfaces/stepInterfaces";
import { toast } from "react-toastify";
import Modal from "../../../Components/Modal/Modal";
import SahraConnections from "../../Sahra/SahraConnections";
import PortfoGroupUserMultiSelectDropdown from "../../../Components/Dropdown/Users/PortfoGroupUserMultiSelectDropdown";
import SimpleTextInput from "../../../Components/SimpleTextInput";
import {
  onNLegsOrderFormValuesChange,
  setNLegsAddOrderFormValues,
  setNLegsOrderTableFetchDataFlag,
} from "../../../Features/NLegs/nLegsSlice";
import {
  INLegsAddOrderFormValues,
  IUNLegsItem,
  NLegSendMode,
} from "../../../Interfaces/nLegsInterfaces";
import { DropdownItem } from "../../../Interfaces/dropdownInterfaces";
import InsWithTypeDropdown from "../../../Components/Dropdown/InsWithTypeDropdown";
import NLegOrderPreviewTable from "../../../Tables/NLegs/NLegOrderPreviewTable";
import {
  previewNLegOrderByVolume,
  submitNLegOrderByVolume,
} from "../../../Requests/nLegsRequests";
import FullScreenLoading from "../../../Components/Loading/FullScreenLoading";
import { getStepInstruments } from "../../../Requests/stepRequests";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import { addSeperatorToInput, stringifyNumbers } from "../../../helper";
import { linkedNLegVolume } from "../../../Utils/nLegVolume";

enum ModalMode {
  OrderPreview,
  SahraConnections,
}

interface IProps {
  legCount: number;
}

const volumeTypeOptions = [
  { id: VolumeCalculationType.FixedAmount, name: "مقدار ثابت" },
];

const getVolumeLabel = (volumeType?: VolumeCalculationType) => {
  switch (volumeType) {
    case VolumeCalculationType.FixedAmount:
      return "حجم پایه";
    case VolumeCalculationType.PortfolioPercent:
      return "درصد پورتفو";
    case VolumeCalculationType.AssetPercent:
      return "درصد دارایی";
    case VolumeCalculationType.TargetPortfolioPercent:
      return "درصد هدف پورتفو";
    case VolumeCalculationType.BuyingPowerPercent:
      return "درصد قدرت خرید";
    default:
      return "حجم";
  }
};

const isFixedAmountVolume = (volumeType?: VolumeCalculationType) =>
  volumeType === VolumeCalculationType.FixedAmount;

const SingleOrder = ({ legCount }: IProps) => {
  const formValues = useAppSelector((store) => store.nLegs.addOrderFormValues);
  const dispatch = useAppDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(ModalMode.OrderPreview);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewValues, setPreviewValues] = useState<
    INLegsAddOrderFormValues[] | null
  >(null);
  const [instrumentOptions, setInstrumentOptions] = useState<
    IStepOptionDropdownItem[]
  >([]);

  useEffect(() => {
    dispatch(
      setNLegsAddOrderFormValues({
        ...formValues,
        uNLegsItems: Array.from(
          { length: legCount },
          () => ({}) as IUNLegsItem,
        ),
        userIds:
          formValues?.userIds ||
          (formValues?.userId ? [formValues.userId] : []),
        volumeType: formValues?.volumeType ?? VolumeCalculationType.FixedAmount,
        sendMode: formValues?.sendMode ?? NLegSendMode.Simultaneous,
        sendPriorities: formValues?.sendPriorities?.length === legCount
          ? formValues.sendPriorities
          : Array(legCount).fill(0),
      } as INLegsAddOrderFormValues),
    );

    getStepInstruments()
      .then((resp) => {
        setInstrumentOptions(resp);
      })
      .catch(packetErrorHandler);
  }, [legCount]);

  const modalCloseHandler = () => {
    if (modalMode === ModalMode.SahraConnections) {
      setModalMode(ModalMode.OrderPreview);
      return;
    }
    setIsModalOpen(false);
    setPreviewValues(null);
  };

  const changeHandler = (
    name: string,
    value: any,
    name2?: string,
    value2?: any,
  ) => {
    dispatch(onNLegsOrderFormValuesChange({ name, value, name2, value2 }));
  };

  const submitHandler = () => {
    if (!previewValues?.length) return;

    setLoading(true);
    submitNLegOrderByVolume(previewValues)
      .then((resp) => {
        if (!resp.data) toast.error(resp.msg);
        else {
          toast.success(resp.msg);
          setIsModalOpen(false);
          setPreviewValues(null);
          dispatch(setNLegsOrderTableFetchDataFlag(true));
          dispatch(
            setNLegsAddOrderFormValues({
              ...formValues,
              uNLegsItems: formValues?.uNLegsItems
                ?.slice(0, legCount)
                .map((i) => ({
                  instrumentId: i.instrumentId,
                  optionOrderType: i.optionOrderType,
                  ratio: i.ratio,
                  contractSize: i.contractSize,
                  orderSide: i.orderSide,
                  volume: i.volume,
                })),
            } as INLegsAddOrderFormValues),
          );
        }
      })
      .catch((e) => toast.error(e.response?.data?.value || e.message))
      .finally(() => {
        setLoading(false);
      });
  };

  const openPreview = () => {
    if (!formValues) return;

    setIsModalOpen(true);
    setModalMode(ModalMode.OrderPreview);
    setPreviewValues(null);
    setPreviewLoading(true);

    previewNLegOrderByVolume({
      ...formValues,
      uNLegsItems: formValues.uNLegsItems.slice(0, legCount),
      volumeType: formValues.volumeType ?? VolumeCalculationType.FixedAmount,
    } as INLegsAddOrderFormValues)
      .then((resp) => setPreviewValues(resp))
      .catch(packetErrorHandler)
      .finally(() => setPreviewLoading(false));
  };

  const removePreviewRowHandler = (userId: number) => {
    setPreviewValues(
      (current) => current?.filter((row) => row.userId !== userId) || current,
    );
  };

  const orderSidesChangeHandler = (name: string, value: any) => {
    if (!formValues) return;

    const newItems = [...formValues.uNLegsItems];

    if (parseInt(name) <= newItems.length) {
      newItems.splice(parseInt(name) - 1, 1, {
        ...newItems[parseInt(name) - 1],
        orderSide: value,
      });
    } else
      newItems.push({
        orderSide: value,
      } as IUNLegsItem);

    dispatch(
      setNLegsAddOrderFormValues({
        ...formValues,
        uNLegsItems: newItems,
      } as INLegsAddOrderFormValues),
    );
  };

  const insChangeHandler = (
    name: string,
    value: any,
    index: number,
    contractSize?: number,
    name2?: string,
    value2?: any,
  ) => {
    const items = [...(formValues?.uNLegsItems || [])];
    if (!items) return;
    if (name2 && value2)
      items.splice(index, 1, {
        ...items[index],
        [name]: value,
        contractSize:
          items[index]?.contractSize && contractSize === undefined
            ? items[index].contractSize
            : contractSize === 0
              ? 1
              : contractSize,
        [name2]: value2,
      } as IUNLegsItem);
    else
      items.splice(index, 1, {
        ...items[index],
        [name]: value,
        contractSize:
          items[index]?.contractSize && contractSize === undefined
            ? items[index].contractSize
            : contractSize === 0
              ? 1
              : contractSize,
      } as IUNLegsItem);
    dispatch(
      onNLegsOrderFormValuesChange({ name: "uNLegsItems", value: items }),
    );
  };

  const getItemContractSize = (item?: IUNLegsItem) => {
    if (item?.contractSize) return item.contractSize;
    if (!item?.instrumentId) return 1;
    return (
      instrumentOptions.find((ins) => ins.id === item.instrumentId)
        ?.contractSize || 1
    );
  };

  const ratioChangeHandler = (name: string, value: any) => {
    const index = parseInt(name);
    const items = [...(formValues?.uNLegsItems || [])];
    const mainCs = getItemContractSize(items[index]);

    items[index] = {
      ...items[index],
      volume: value,
      contractSize: mainCs,
      ratio: value,
    };
    items.forEach((item, i) => {
      if (i === index || !item?.ratio) return;

      const itemCs = getItemContractSize(item);

      items[i] = {
        ...item,
        volume:
          itemCs && items[index].ratio
            ? linkedNLegVolume(items[index].volume, mainCs, itemCs, items[index].ratio, item.ratio)
            : 0,
        contractSize: itemCs,
      };
    });

    dispatch(
      onNLegsOrderFormValuesChange({ name: "uNLegsItems", value: items }),
    );
  };

  const getRatioSquare = (value: any, index: number) => {
    const orderSide = formValues?.uNLegsItems?.[index]?.orderSide;
    const sideColorClass =
      orderSide === StepOrderSide.Buy
        ? "text-green-700"
        : orderSide === StepOrderSide.Sell
          ? "text-red-700"
          : "text-gray-600";

    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className={`text-xs ${sideColorClass}`}>
          {stringifyNumbers(index + 1)}
        </span>
        <SimpleTextInput
          name={index?.toString()}
          value={addSeperatorToInput(value, "priceWithDecimal") || ""}
          onChange={ratioChangeHandler}
          kind="priceWithDecimals"
          className={`!max-w-[64px] !w-full text-center pr-0 ${
            orderSide === StepOrderSide.Buy
              ? "border-green-700 focus:border-green-700"
              : orderSide === StepOrderSide.Sell
                ? "border-red-700 focus:border-red-700"
                : "border-gray-600 focus:border-gray-600"
          }`}
        />
      </div>
    );
  };

  const volumeChangeHandler = (_: string, value: any, index: number) => {
    const items = [...(formValues?.uNLegsItems || [])];
    const mainCs = getItemContractSize(items[index]);

    items[index] = {
      ...items[index],
      volume: value,
      contractSize: mainCs,
    };
    items.forEach((item, i) => {
      if (i === index || !item?.ratio) return;

      const itemCs = getItemContractSize(item);

      items[i] = {
        ...item,
        volume:
          itemCs && items[index].ratio
            ? linkedNLegVolume(items[index].volume, mainCs, itemCs, items[index].ratio, item.ratio)
            : 0,
        contractSize: itemCs,
      };
    });

    dispatch(
      onNLegsOrderFormValuesChange({ name: "uNLegsItems", value: items }),
    );
  };

  const previewModalUI = () => (
    <div className="flex flex-col justify-start items-center gap-8">
      {previewLoading ? <FullScreenLoading /> : <></>}
      <NLegOrderPreviewTable
        values={previewValues}
        onRemoveRow={removePreviewRowHandler}
        instrumentOptions={instrumentOptions}
        showVolumeMethod
      />
      <Button
        text="ارسال"
        onClick={() => setModalMode(ModalMode.SahraConnections)}
        variant="contained"
        disabled={previewLoading || !previewValues?.length}
      />
    </div>
  );

  const connectionsModalUI = () => (
    <div className="flex flex-col justify-start items-center gap-3">
      <SahraConnections
        initialSearchParam={{
          userIds: previewValues?.map((row) => row.userId).filter(
            (userId): userId is number => typeof userId === "number" && userId > 0,
          ) || [],
        }}
      />
      <Button
        text="ارسال"
        onClick={submitHandler}
        variant="contained"
        loading={loading}
        disabled={!previewValues?.length}
      />
    </div>
  );

  const checkBtnDisabled = () => {
    if (
      !formValues?.blockSizePercent ||
      !formValues?.marginPercent ||
      !formValues?.maxPairPosition ||
      !formValues?.userIds ||
      !formValues?.userIds.length ||
      !formValues?.volumeType ||
      !formValues?.uNLegsItems ||
      formValues?.uNLegsItems.length < legCount ||
      !formValues?.uNLegsItems.every((u) => u?.instrumentId) ||
      !formValues?.uNLegsItems.every((u) => u?.ratio) ||
      !formValues?.uNLegsItems.every((u) => u?.orderSide) ||
      !formValues?.uNLegsItems.every((u) => u?.volume)
    )
      return true;
    if (formValues.sendMode === NLegSendMode.Sequential &&
      (!formValues.sendPriorities ||
       formValues.sendPriorities.length !== legCount ||
       new Set(formValues.sendPriorities).size !== legCount ||
       formValues.sendPriorities.some((priority) => priority < 1 || priority > legCount) ||
       new Set(formValues.uNLegsItems.slice(0, legCount).map((item) => item.instrumentId)).size !== legCount))
      return true;
    return false;
  };
  return (
    <Form className="!w-80 gap-3 p-2 px-2">
      {loading ? <FullScreenLoading /> : <></>}
      {isModalOpen ? (
        <Modal title={modalMode === ModalMode.OrderPreview ? "پیش‌نمایش سفارش" : "اتصالات صحرا"}
          onClose={modalCloseHandler}
          UI={modalMode === ModalMode.OrderPreview ? previewModalUI() : connectionsModalUI()}
        />
      ) : (
        <></>
      )}

      <PortfoGroupUserMultiSelectDropdown
        label="نام اکانت ها"
        name="userIds"
        value={formValues?.userIds}
        onChange={changeHandler}
        withTagBox
        extraLabel
      />
      <div
        className={`${
          legCount < 4
            ? "flex flex-row justify-start items-center"
            : "grid grid-cols-2"
        }  gap-1 w-full`}
      >
        {new Array(legCount).fill(null).map((_, i) => (
          <StaticDropdown
            dropdown={{
              name: (i + 1).toString(),
              placeholder: `جهت ${stringifyNumbers(i + 1)}`,
              extraLabel: true,
            }}
            onChange={orderSidesChangeHandler}
            options={[
              { id: StepOrderSide.Buy, name: "خرید" },
              { id: StepOrderSide.Sell, name: "فروش" },
            ]}
            value={formValues?.uNLegsItems[i]?.orderSide}
          />
        ))}
      </div>

      <>
        {new Array(legCount).fill(null).map((_, i) => (
          <InsWithTypeDropdown
            insValue={formValues?.uNLegsItems[i]?.instrumentId}
            onInsChange={(name: string, value: any, cs: number) =>
              insChangeHandler(name, value, i, cs)
            }
            onTypeChange={(name: string, value: any) =>
              insChangeHandler(name, value, i)
            }
            insInfo={{
              name: "instrumentId",
              placeholder: `نماد ${stringifyNumbers(i + 1)}`,
            }}
            typeInfo={{
              name: "optionOrderType",
              placeholder: `نوع معامله اختیار ${stringifyNumbers(i + 1)}`,
            }}
            typeValue={formValues?.uNLegsItems[i]?.optionOrderType}
            instrumentOptions={instrumentOptions}
            extraLabel
          />
        ))}
      </>

      <div className="flex flex-row justify-between items-end gap-2 w-full">
        <div className="text-xs pb-1.5">نسبت اجرا:</div>
        {new Array(legCount)
          .fill(null)
          .map((_, i) => getRatioSquare(formValues?.uNLegsItems[i]?.ratio, i))}
      </div>
      <StaticDropdown
        dropdown={{
          name: "volumeType",
          placeholder: "روش تعیین حجم",
          extraLabel: true,
        }}
        onChange={changeHandler}
        value={formValues?.volumeType}
        options={volumeTypeOptions}
      />

      <div
        className={`w-full gap-2 ${
          isFixedAmountVolume(formValues?.volumeType)
            ? "grid grid-cols-2"
            : "flex flex-col"
        }`}
      >
        {new Array(legCount)
          .fill(null)
          .map((_, i) =>
            !isFixedAmountVolume(formValues?.volumeType) && i > 0 ? null : (
              <TextInput
                key={i}
                name="volume"
                label={
                  i === 0
                    ? getVolumeLabel(formValues?.volumeType)
                    : `حجم ${stringifyNumbers(i + 1)}`
                }
                onChange={(...inputs) => volumeChangeHandler(...inputs, i)}
                value={
                  Math.round(
                    parseInt(
                      formValues?.uNLegsItems[i]?.volume?.toString() || "",
                    ),
                  )?.toString() || ""
                }
                kind="priceWithDecimal"
                extraLabel
              />
            ),
          )}
      </div>

      <TextInput
        label="حداکثر بهای تمام شده"
        value={formValues?.maxPairPosition?.toString() || ""}
        name="maxPairPosition"
        onChange={changeHandler}
        kind="negativeDecimalNumbers"
        extraLabel
      />

      <TextInput
        label="حجم هر بلوک به صورت درصدی"
        name="blockSizePercent"
        onChange={changeHandler}
        value={formValues?.blockSizePercent?.toString() || ""}
        kind="percent"
        extraLabel
      />
      <TextInput
        label="مارجین به صورت درصدی"
        name="marginPercent"
        onChange={changeHandler}
        value={formValues?.marginPercent?.toString() || ""}
        kind="percent"
        extraLabel
      />
      <StaticDropdown
        dropdown={{ name: "sendMode", placeholder: "نوع ارسال", extraLabel: true }}
        onChange={(name, value) => {
          if (value === NLegSendMode.Sequential)
            changeHandler(name, value, "sendPriorities", Array(legCount).fill(0));
          else changeHandler(name, value);
        }}
        value={formValues?.sendMode ?? NLegSendMode.Simultaneous}
        options={[
          { id: NLegSendMode.Simultaneous, name: "هم‌زمان" },
          { id: NLegSendMode.Sequential, name: "ترتیبی" },
        ]}
      />
      <>{formValues?.sendMode === NLegSendMode.Sequential ? (
        <div className={`${legCount < 4 ? "flex flex-row justify-start items-center" : "grid grid-cols-2"} gap-1 w-full`}>
          {Array.from({ length: legCount }, (_, priorityIndex) => {
            const selectedIndex = formValues.sendPriorities?.findIndex(
              (priority) => priority === priorityIndex + 1,
            ) ?? -1;
            return (
              <div key={priorityIndex} className="min-w-0 flex-1">
                <StaticDropdown
                  dropdown={{
                    name: `sendPriority-${priorityIndex}`,
                    placeholder: `اولویت ${stringifyNumbers(priorityIndex + 1)}`,
                    extraLabel: true,
                  }}
                  value={selectedIndex < 0 ? null : selectedIndex}
                  options={formValues.uNLegsItems.slice(0, legCount).map((item, index) => {
                    const assignedPriority = formValues.sendPriorities?.[index];
                    const isAssignedElsewhere = assignedPriority !== undefined
                      && assignedPriority !== 0
                      && index !== selectedIndex;
                    const instrumentName = instrumentOptions.find((option) => option.id === item.instrumentId)?.name
                      || `نماد ${stringifyNumbers(index + 1)}`;
                    return {
                      id: index,
                      name: isAssignedElsewhere
                        ? `${instrumentName} (اولویت ${stringifyNumbers(assignedPriority)})`
                        : instrumentName,
                      disabled: isAssignedElsewhere,
                    };
                  }).filter((option) => Boolean(formValues.uNLegsItems[option.id]?.instrumentId))}
                  onChange={(_, value) => {
                    const next = [...(formValues.sendPriorities || Array(legCount).fill(0))];
                    const newIndex = value === null ? -1 : Number(value);
                    if (newIndex >= 0 && next[newIndex] !== 0 && newIndex !== selectedIndex) return;
                    if (selectedIndex >= 0) next[selectedIndex] = 0;
                    if (newIndex >= 0) next[newIndex] = priorityIndex + 1;
                    changeHandler("sendPriorities", next);
                  }}
                />
              </div>
            );
          })}
        </div>
      ) : null}</>

      <Button
        text="ارسال"
        onClick={openPreview}
        variant="contained"
        disabled={checkBtnDisabled()}
      />
    </Form>
  );
};

export default SingleOrder;
